import LinkButton from '@carvana/ds-link-button';
import cx from 'classnames';
import { ChevronDown, ZoomIn } from 'lucide-react';
import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { Text } from '@/vdp/styles';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConfiguration } from '~/context/common/StaticContext';
import { useModalContext } from '~/context/vdp/ModalContext/ModalContext';
import useIsClampEnabled from '~/hooks/common/ui/useIsClampEnabled';
import type { WhyBuyNarrativeItem } from '~/hooks/vdp/selectors/useWhyBuyNarratives';
import getWhyBuyMediaProps from '~/utilities/vdp/getWhyBuyMediaProps';
import useWhyBuyCardVideo from './hooks/useWhyBuyCardVideo';
import useWhyBuyStillImage from './hooks/useWhyBuyStillImage';
import WhyBuyMakePlaceholder from './WhyBuyMakePlaceholder';
import {
  ImageryCard,
  ImageryCardContent,
  ImageryWrapper,
  MagnifierButton,
  StyledImage,
  StyledVideo
} from './WhyBuyWithImageryCard.styles';

type OwnProps = {
  bulletPoint: WhyBuyNarrativeItem;
  isActive: boolean;
  isClamped: boolean;
  onActivate: () => void;
  setIsClamped: Dispatch<SetStateAction<boolean>>;
};

const IMAGE_HEIGHT = 170;

export default function WhyBuyWithImageryCard({
  bulletPoint,
  isActive,
  isClamped,
  onActivate,
  setIsClamped
}: OwnProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { shouldUseTitleCasing } = useConfiguration();
  const { setWhyBuyModalOpen } = useModalContext();
  const trackEvent = useTrackEvent();
  const [failedVideoUrl, setFailedVideoUrl] = useState<string>();
  const isClampEnabled = useIsClampEnabled(textRef, !isClamped);
  const { imageUrl, videoUrl } = useMemo(
    () => getWhyBuyMediaProps(bulletPoint.media, { height: IMAGE_HEIGHT }),
    [bulletPoint.media]
  );
  const { isFailed: isImageFailed, url: loadedImageUrl } = useWhyBuyStillImage(
    imageUrl,
    'WhyBuyWithImageryCard'
  );
  const hasMedia = Boolean(imageUrl || videoUrl);
  const isVideoFailed = Boolean(videoUrl) && failedVideoUrl === videoUrl;
  const isVideoShown = Boolean(videoUrl) && !isVideoFailed;
  const isMediaShown = isVideoShown || Boolean(loadedImageUrl);
  const isPlaceholderShown =
    !isMediaShown && (!hasMedia || isVideoFailed || isImageFailed);
  const handleMediaError = useCallback(
    () => setFailedVideoUrl(videoUrl),
    [videoUrl]
  );
  const { onError, onMouseEnter, onMouseLeave, videoRef } = useWhyBuyCardVideo({
    cardRef,
    isActive,
    isMobile,
    onActivate,
    onMediaError: handleMediaError,
    src: videoUrl
  });

  const handleClick = () => {
    setWhyBuyModalOpen(true, { openTo: bulletPoint.id });
    trackEvent('Why Buy Feature', {
      description: 'why buy feature',
      item: bulletPoint.title
    });
  };

  const toggleClamp = (event: MouseEvent) => {
    event.stopPropagation();
    setIsClamped(previous => !previous);
    trackEvent('Why Buy Feature', {
      description: 'why buy feature',
      item: `show ${isClamped ? 'more' : 'less'} truncated why buy feature`
    });
  };

  const showMoreLabel = `Show ${isClamped ? 'more' : 'less'}`;

  return (
    <ImageryCard
      data-adobe-id={null}
      data-analytics-id={null}
      data-testid="why-buy-imagery-card"
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={cardRef}
    >
      <ImageryWrapper>
        {isVideoShown && (
          <StyledVideo
            data-testid="why-buy-imagery-video"
            draggable={false}
            muted
            onError={onError}
            playsInline
            preload="metadata"
            ref={videoRef}
            src={videoUrl}
          />
        )}
        {loadedImageUrl && (
          <StyledImage
            alt={bulletPoint.title}
            data-testid="why-buy-imagery-image"
            draggable={false}
            loading="lazy"
            src={loadedImageUrl}
          />
        )}
        {isPlaceholderShown && <WhyBuyMakePlaceholder />}
        <MagnifierButton
          aria-label={`Learn more about ${bulletPoint.title}`}
          data-adobe-id={null}
          data-analytics-id={null}
          className={cx(
            isPlaceholderShown && 'opacity-0 focus-visible:opacity-100'
          )}
          data-testid="why-buy-magnifier"
          type="button"
        >
          <ZoomIn aria-hidden="true" size={16} />
        </MagnifierButton>
      </ImageryWrapper>
      <ImageryCardContent>
        <div
          className={cx(
            'line-clamp-6',
            isClampEnabled && !isClamped && 'line-clamp-none'
          )}
          ref={textRef}
        >
          <h3 className="mb-lg text-text-strong t-heading-sm">
            {bulletPoint.title}
          </h3>
          <Text>{bulletPoint.content}</Text>
        </div>
        {isClampEnabled && (
          <LinkButton
            aria-expanded={!isClamped}
            className="mt-auto inline-flex items-center gap-sm self-start text-text-strong"
            data-adobe-id={null}
            data-analytics-id={null}
            data-testid="why-buy-show-more"
            onClick={toggleClamp}
            size="sm"
          >
            {shouldUseTitleCasing
              ? `Show ${isClamped ? 'More' : 'Less'}`
              : showMoreLabel}
            <ChevronDown
              aria-hidden="true"
              className={cx(!isClamped && 'rotate-180')}
              size={16}
            />
          </LinkButton>
        )}
      </ImageryCardContent>
    </ImageryCard>
  );
}
